/**
 * Price calculation utilities for Order Pricing Display
 * Handles line item calculations, order totals, and currency formatting
 */

export interface PricingData {
  largeIceCubePriceTHB: number
  smallIceCubePriceTHB: number
  crushedIcePriceTHB: number
}

export interface ProductQuantities {
  largeIceCubeAmount: number
  smallIceCubeAmount: number
  crushedIceAmount: number
  coarselyCrushedIceAmount: number
}

export interface LineItemCalculation {
  quantity: number
  pricePerUnit: number
  totalPrice: number
}

export interface OrderTotalCalculation {
  largeIceCube: LineItemCalculation
  smallIceCube: LineItemCalculation
  crushedIce: LineItemCalculation
  coarselyCrushedIce: LineItemCalculation
  grandTotal: number
}

/**
 * Calculate line item total for a single product type
 * @param quantity - Quantity of the product
 * @param pricePerUnit - Price per unit in THB
 * @returns Line item calculation with quantity, price per unit, and total
 */
export function calculateLineItem(
  quantity: number,
  pricePerUnit: number
): LineItemCalculation {
  // Handle null/undefined/NaN values for quantity
  let safeQuantity = 0
  if (quantity != null && !isNaN(quantity) && isFinite(quantity)) {
    safeQuantity = Math.max(0, Math.floor(quantity)) // Ensure integer and non-negative
  }

  // Handle null/undefined/NaN values for price
  let safePrice = 0
  if (pricePerUnit != null && !isNaN(pricePerUnit) && isFinite(pricePerUnit)) {
    safePrice = Math.max(0, pricePerUnit) // Ensure non-negative
  }

  // Calculate total with overflow protection
  let totalPrice = 0
  try {
    totalPrice = safeQuantity * safePrice

    // Check for overflow or invalid calculation
    if (!isFinite(totalPrice) || totalPrice > Number.MAX_SAFE_INTEGER) {
      console.warn('calculateLineItem: Calculation overflow detected')
      totalPrice = 0
    }
  } catch (error) {
    console.error('calculateLineItem: Error in calculation:', error)
    totalPrice = 0
  }

  return {
    quantity: safeQuantity,
    pricePerUnit: safePrice,
    totalPrice,
  }
}

/**
 * Calculate order total for all product types
 * @param quantities - Product quantities for all ice cube types
 * @param pricing - Pricing data for all product types
 * @returns Complete order calculation breakdown
 */
export function calculateOrderTotal(
  quantities: ProductQuantities,
  pricing: PricingData
): OrderTotalCalculation {
  // Validate inputs
  if (!quantities || !pricing) {
    console.warn('calculateOrderTotal: Invalid input data')
    const emptyCalculation: LineItemCalculation = {
      quantity: 0,
      pricePerUnit: 0,
      totalPrice: 0,
    }
    return {
      largeIceCube: emptyCalculation,
      smallIceCube: emptyCalculation,
      crushedIce: emptyCalculation,
      coarselyCrushedIce: emptyCalculation,
      grandTotal: 0,
    }
  }

  try {
    // Calculate line items for each product type
    const largeIceCube = calculateLineItem(
      quantities.largeIceCubeAmount,
      pricing.largeIceCubePriceTHB
    )

    const smallIceCube = calculateLineItem(
      quantities.smallIceCubeAmount,
      pricing.smallIceCubePriceTHB
    )

    const crushedIce = calculateLineItem(
      quantities.crushedIceAmount,
      pricing.crushedIcePriceTHB
    )

    // Note: coarsely crushed ice uses the same price as crushed ice
    const coarselyCrushedIce = calculateLineItem(
      quantities.coarselyCrushedIceAmount,
      pricing.crushedIcePriceTHB
    )

    // Calculate grand total with overflow protection
    let grandTotal = 0
    try {
      grandTotal =
        largeIceCube.totalPrice +
        smallIceCube.totalPrice +
        crushedIce.totalPrice +
        coarselyCrushedIce.totalPrice

      // Check for overflow
      if (!isFinite(grandTotal) || grandTotal > Number.MAX_SAFE_INTEGER) {
        console.warn('calculateOrderTotal: Grand total overflow detected')
        grandTotal = 0
      }
    } catch (error) {
      console.error(
        'calculateOrderTotal: Error calculating grand total:',
        error
      )
      grandTotal = 0
    }

    return {
      largeIceCube,
      smallIceCube,
      crushedIce,
      coarselyCrushedIce,
      grandTotal,
    }
  } catch (error) {
    console.error('calculateOrderTotal: Unexpected error:', error)
    const emptyCalculation: LineItemCalculation = {
      quantity: 0,
      pricePerUnit: 0,
      totalPrice: 0,
    }
    return {
      largeIceCube: emptyCalculation,
      smallIceCube: emptyCalculation,
      crushedIce: emptyCalculation,
      coarselyCrushedIce: emptyCalculation,
      grandTotal: 0,
    }
  }
}

/**
 * Format currency amount for display with Thai Baht symbol
 * @param amount - Amount in THB
 * @returns Formatted string with ฿ symbol and number separators
 */
export function formatCurrency(amount: number): string {
  // Handle null/undefined/NaN values
  if (amount == null || isNaN(amount)) {
    return '฿0.00'
  }

  // Handle Infinity values
  if (!isFinite(amount)) {
    console.warn('formatCurrency: Received infinite value, returning ฿0.00')
    return '฿0.00'
  }

  // Ensure amount is not negative for display
  const safeAmount = Math.max(0, amount)

  // Handle extremely large numbers that might cause formatting issues
  if (safeAmount > Number.MAX_SAFE_INTEGER) {
    console.warn('formatCurrency: Amount exceeds safe integer limit')
    return '฿999,999,999.99'
  }

  try {
    // Format with Thai locale for proper number separators
    const formatted = safeAmount.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

    return `฿${formatted}`
  } catch (error) {
    console.error('formatCurrency: Error formatting currency:', error)
    // Fallback to basic formatting
    return `฿${safeAmount.toFixed(2)}`
  }
}

/**
 * Validate pricing data to ensure all required fields are present and valid
 * @param pricing - Pricing data to validate
 * @returns True if pricing data is valid
 */
export function validatePricingData(pricing: PricingData): boolean {
  if (!pricing) return false

  return (
    typeof pricing.largeIceCubePriceTHB === 'number' &&
    typeof pricing.smallIceCubePriceTHB === 'number' &&
    typeof pricing.crushedIcePriceTHB === 'number' &&
    pricing.largeIceCubePriceTHB >= 0 &&
    pricing.smallIceCubePriceTHB >= 0 &&
    pricing.crushedIcePriceTHB >= 0
  )
}

/**
 * Validate product quantities to ensure all values are valid numbers
 * @param quantities - Product quantities to validate
 * @returns True if quantities are valid
 */
export function validateProductQuantities(
  quantities: ProductQuantities
): boolean {
  if (!quantities) return false

  return (
    typeof quantities.largeIceCubeAmount === 'number' &&
    typeof quantities.smallIceCubeAmount === 'number' &&
    typeof quantities.crushedIceAmount === 'number' &&
    typeof quantities.coarselyCrushedIceAmount === 'number' &&
    isFinite(quantities.largeIceCubeAmount) &&
    isFinite(quantities.smallIceCubeAmount) &&
    isFinite(quantities.crushedIceAmount) &&
    isFinite(quantities.coarselyCrushedIceAmount) &&
    quantities.largeIceCubeAmount >= 0 &&
    quantities.smallIceCubeAmount >= 0 &&
    quantities.crushedIceAmount >= 0 &&
    quantities.coarselyCrushedIceAmount >= 0
  )
}

/**
 * Sanitize input values to prevent calculation errors
 * @param value - Input value to sanitize
 * @returns Safe number value
 */
export function sanitizeNumericInput(value: any): number {
  // Handle null, undefined, or non-numeric values
  if (value == null || typeof value !== 'number') {
    return 0
  }

  // Handle NaN, Infinity, or negative values
  if (isNaN(value) || !isFinite(value) || value < 0) {
    return 0
  }

  // Handle extremely large values
  if (value > Number.MAX_SAFE_INTEGER) {
    console.warn('sanitizeNumericInput: Value exceeds safe integer limit')
    return 0
  }

  // Return floor value for quantities (no decimals)
  return Math.floor(value)
}

/**
 * Create empty calculation result for error cases
 * @returns Empty LineItemCalculation
 */
export function createEmptyCalculation(): LineItemCalculation {
  return {
    quantity: 0,
    pricePerUnit: 0,
    totalPrice: 0,
  }
}

/**
 * Create empty order total for error cases
 * @returns Empty OrderTotalCalculation
 */
export function createEmptyOrderTotal(): OrderTotalCalculation {
  const emptyCalculation = createEmptyCalculation()
  return {
    largeIceCube: emptyCalculation,
    smallIceCube: emptyCalculation,
    crushedIce: emptyCalculation,
    coarselyCrushedIce: emptyCalculation,
    grandTotal: 0,
  }
}
